<!--
	Shared character-limited text field (LSP-16848). It bundles the length-limit rules so fields stop
	hand-rolling them: a live code-point counter (an emoji is 1, never UTF-16 `.length`), soft overflow
	(no native maxlength and no truncation — the counter and field turn red past the limit), and a
	code-point-aware rule that blocks save through the parent form.

	Soft overflow is the default and the repo-wide rule (root CLAUDE.md, "Form field length limits
	(maxLength) — warn, never hard-cap", LSP-16465). A field with an explicit decision to hard-cap
	instead can pass `hard-cap` — see CharacterLimitProps.hardCap. Nothing else renders a native
	maxlength; a caller's own `maxlength` attribute is still stripped by sanitizeWrapperOwnedAttrs.

	Save pattern — the action button stays enabled and the click no-ops while the form is invalid
	(root CLAUDE.md, "Form action buttons — always enabled, no-op when invalid"):

		<v-form ref="form">
			<CharacterLimitedTextField v-model="name" :max-length="FIELD_LENGTHS.TEXT" label="Name (Required)" :rules="nameRules" />
		</v-form>

		async function save() {
			const { valid } = await form.value.validate();
			if (!valid) return;
			...
		}

	Everything else — v-model, label, placeholder, density, v-test, events, slots — passes straight
	through to v-text-field. Two exceptions, both bound before v-bind="passthroughAttrs" below so a
	caller's own binding still wins: persistent-counter (R1), on so the live counter is never
	focus-gated; and aria-invalid (break-it round 4), reflecting the field's own live validity so a
	screen-reader user is told when the soft over-limit refusal blocks save — see useCharacterLimit's
	`ariaInvalid` doc comment.

	The #counter slot below reuses that same aria-invalid signal to color the counter text (review
	finding: Vuetify's own VCounter only reddens when ITS OWN value exceeds max, so a field invalid
	for a DIFFERENT reason — e.g. a caller's server-unit backstop rule failing while the code-point
	count is still under the limit — showed a red border and error message next to a counter that
	looked fine, which read as self-contradictory). No caller of this component supplies its own
	#counter slot today, so this never collides with the generic slot-forwarding loop below.
-->
<template>
	<v-text-field
		ref="fieldRef"
		persistent-counter
		:aria-invalid="ariaInvalid"
		v-bind="passthroughAttrs"
		:counter="resolvedMaxLength"
		:counter-value="counterValue"
		:maxlength="hardCapAttribute"
		:rules="mergedRules">
		<!-- Bold the trailing (Required)/(Optional) tag on the label (LSP-16777), matching BaseTextField. -->
		<template v-if="$attrs.label && !$slots.label" #label>
			<FieldLabel :label="String($attrs.label)" />
		</template>
		<template #counter="{ counter }">
			<span :class="{ 'text-error': ariaInvalid === 'true' }">{{ counter }}</span>
		</template>
		<template v-for="(_, slot) in $slots" #[slot]="scope" :key="slot">
			<slot :name="slot" v-bind="scope || {}"></slot>
		</template>
	</v-text-field>
</template>

<script setup lang="ts">
	import { computed, useTemplateRef } from "vue";
	import type { VTextField } from "vuetify/components";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { useCharacterLimit, type CharacterLimitProps } from "@/composables/useCharacterLimit";

	// inheritAttrs must stay false: we bind a FILTERED copy of $attrs, and Vue would otherwise
	// auto-apply the unfiltered set to the root element and put a caller's maxlength back.
	defineOptions({ inheritAttrs: false });

	const props = withDefaults(defineProps<CharacterLimitProps>(), {
		rules: () => [],
		overLimitMessage: undefined,
		trim: true,
		hardCap: false
	});

	const fieldRef = useTemplateRef<InstanceType<typeof VTextField>>("fieldRef");
	const { passthroughAttrs, counterValue, resolvedMaxLength, ariaInvalid, mergedRules, validate, resetValidation, isValid, focus, blur } =
		useCharacterLimit("CharacterLimitedTextField", fieldRef, props);

	// The ONLY native maxlength this component will ever render, and only when a caller opts in with
	// `hard-cap` (see CharacterLimitProps.hardCap). Bound as a component-owned prop rather than let
	// through `passthroughAttrs`, so `sanitizeWrapperOwnedAttrs` keeps stripping a caller's stray
	// `maxlength` exactly as before — opting in has to be deliberate and named. `undefined` makes Vue
	// omit the attribute entirely, so the default path renders no maxlength at all. Falls back to
	// undefined when the limit is unusable (`resolvedMaxLength` is 0), matching the composable's
	// fail-closed handling rather than emitting `maxlength="0"`, which would freeze the field.
	const hardCapAttribute = computed(() => (props.hardCap && resolvedMaxLength.value > 0 ? resolvedMaxLength.value : undefined));

	// Ref-based validation and focus for callers outside a v-form, or that focus the field
	// programmatically (see ChecklistItemEditor). Vue's expose proxy does not forward the inner
	// component's own methods, so each one needed here has to be re-exposed explicitly.
	defineExpose({ validate, resetValidation, isValid, focus, blur });
</script>
